import React, { useState, useEffect } from 'react';
import {
  ResponsiveContainer, BarChart, Bar, LineChart, Line, AreaChart, Area,
  XAxis, YAxis, CartesianGrid, Tooltip, Cell,
} from 'recharts';
import { Lock, TrendingUp, TrendingDown, Minus, ChevronDown, ChevronRight, Target, DollarSign, Users, Layers } from 'lucide-react';

// Fixed categorical slot order (validated palette — do not reorder or restyle per-chart)
const POS = ['QB', 'RB', 'WR', 'TE', 'K', 'DEF'];
const POS_COLOR = {
  QB: '#2a78d6', RB: '#1baf7a', WR: '#eda100', TE: '#008300', K: '#4a3aa7', DEF: '#e34948',
};
const INK = { primary: '#0b0b0b', secondary: '#52514e', muted: '#898781', grid: '#e1e0d9' };

const fmt$ = (v) => `$${Math.round(v)}`;
const pct = (v) => `${Math.round(v * 100)}%`;

const tooltipStyle = {
  contentStyle: { borderRadius: 8, border: '1px solid #e1e0d9', fontSize: 12, background: '#fff' },
  labelStyle: { color: INK.secondary, fontWeight: 600 },
};

const Card = ({ title, subtitle, children, className = '' }) => (
  <div className={`bg-white rounded-xl border border-gray-200 shadow-sm p-4 sm:p-5 ${className}`}>
    {title && <h3 className="text-sm font-bold text-gray-900">{title}</h3>}
    {subtitle && <p className="text-xs text-gray-500 mb-3">{subtitle}</p>}
    {children}
  </div>
);

const Stat = ({ label, value, sub, icon: Icon }) => (
  <div className="bg-white rounded-xl border border-gray-200 shadow-sm px-4 py-3 flex items-center gap-3">
    {Icon && <Icon className="w-5 h-5 text-gray-400 flex-shrink-0" />}
    <div>
      <div className="text-2xl font-bold text-gray-900 leading-tight">{value}</div>
      <div className="text-xs font-medium text-gray-500">{label}</div>
      {sub && <div className="text-[11px] text-gray-400">{sub}</div>}
    </div>
  </div>
);

// tiny inline positional-DNA bar (plain divs; 2px white gaps between fills)
const DnaBar = ({ mix, label }) => (
  <div className="flex items-center gap-2">
    <span className="w-14 text-[11px] text-gray-500 flex-shrink-0">{label}</span>
    <div className="flex-1 flex h-4 rounded overflow-hidden bg-gray-100">
      {POS.map((p) => (
        <div
          key={p}
          title={`${p} ${pct(mix[p] || 0)}`}
          style={{ width: `${(mix[p] || 0) * 100}%`, background: POS_COLOR[p], borderRight: '2px solid #fff' }}
        />
      ))}
    </div>
    <span className="w-20 text-[10px] text-gray-500 text-right flex-shrink-0">
      RB {pct(mix.RB || 0)} · WR {pct(mix.WR || 0)}
    </span>
  </div>
);

const PosLegend = () => (
  <div className="flex flex-wrap gap-x-3 gap-y-1 text-[11px] text-gray-700 mb-2">
    {POS.map((p) => (
      <span key={p} className="inline-flex items-center gap-1">
        <span className="w-2.5 h-2.5 rounded-sm" style={{ background: POS_COLOR[p] }} />{p}
      </span>
    ))}
  </div>
);

// keep-probability distribution strip: light band = 5–95%, dark = 25–75%,
// tick = median. Sequential single-hue per the viz system.
const ProbStrip = ({ d, w = 64 }) => {
  if (!d) return null;
  const H = 12;
  const x = (v) => v * w;
  return (
    <svg width={w} height={H} className="flex-shrink-0" role="img"
      aria-label={`keep probability 5th–95th percentile ${Math.round(d.q05 * 100)}–${Math.round(d.q95 * 100)}%`}>
      <title>{`5–95%: ${Math.round(d.q05 * 100)}–${Math.round(d.q95 * 100)} · middle half: ${Math.round(d.q25 * 100)}–${Math.round(d.q75 * 100)} · median ${Math.round(d.q50 * 100)}%`}</title>
      <line x1={0} y1={H / 2} x2={w} y2={H / 2} stroke="#e1e0d9" strokeWidth="1" />
      <rect x={x(d.q05)} y={H / 2 - 2} width={Math.max(1, x(d.q95) - x(d.q05))} height={4} rx={2} fill="#cde2fb" />
      <rect x={x(d.q25)} y={H / 2 - 3.5} width={Math.max(1.5, x(d.q75) - x(d.q25))} height={7} rx={2} fill="#86b6ef" />
      <rect x={Math.min(w - 2, x(d.q50))} y={0.5} width={2} height={H - 1} rx={1} fill="#1c5cab" />
    </svg>
  );
};

const scarcityBadge = (delta) => {
  if (delta >= 1.5) return { txt: 'scarcer than usual', cls: 'bg-rose-50 text-rose-700 border-rose-200', Icon: TrendingDown };
  if (delta <= -1.5) return { txt: 'more supply than usual', cls: 'bg-emerald-50 text-emerald-700 border-emerald-200', Icon: TrendingUp };
  return { txt: 'normal supply', cls: 'bg-gray-50 text-gray-600 border-gray-200', Icon: Minus };
};

export default function DraftWarRoom() {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [openTeam, setOpenTeam] = useState(null);
  const [posTab, setPosTab] = useState('WR');
  const [playerQuery, setPlayerQuery] = useState('');

  useEffect(() => {
    fetch('/draft-intel.json')
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then(setData)
      .catch((e) => setError(String(e)));
  }, []);

  if (error) return <div className="p-8 text-sm text-rose-600">Couldn’t load draft-intel.json — run <code>node scripts/build/buildDraftIntel.js</code>. ({error})</div>;
  if (!data) return <div className="p-8 text-sm text-gray-500">Loading the war room…</div>;

  const { league, managers, draft2026, keepProbByBucket, seasons, market } = data;
  const lastYear = seasons[seasons.length - 1];
  const recent5 = league.posSpendByYear.filter((r) => r.year > lastYear - 5);

  // ---- league keep-drag baseline: 5-yr avg keeper count/$ per position
  const histKept = {};
  POS.forEach((p) => {
    histKept[p] = {
      n: recent5.reduce((s, r) => s + r[`${p}_keptN`], 0) / recent5.length,
      $: recent5.reduce((s, r) => s + r[`${p}_kept`], 0) / recent5.length,
    };
  });

  // ---- chart datasets
  const spendData = league.posSpendByYear.map((r) => {
    const o = { year: r.year };
    POS.forEach((p) => { o[p] = r[p]; });
    return o;
  });
  const keeperDrag = league.posSpendByYear.map((r) => ({
    year: r.year, kept$: r.totalKept, keptN: r.keptCount,
  }));
  const maxRank = Math.max(...['QB', 'RB', 'WR', 'TE'].map((p) => league.priceCurves[p].length));
  const curveData = Array.from({ length: maxRank }, (_, i) => {
    const o = { rank: i + 1 };
    ['QB', 'RB', 'WR', 'TE'].forEach((p) => {
      const pt = league.priceCurves[p][i];
      if (pt) o[p] = pt.recent;
    });
    return o;
  });
  const probData = keepProbByBucket.filter((b) => b.n > 0).map((b) => ({
    bucket: `$${b.range[0]}–${b.range[1] === 999 ? '+' : b.range[1]}`, p: Math.round(b.p * 100), n: b.n,
  }));

  const teams = draft2026.perTeam.slice().sort((a, b) => b.projectedBudget - a.projectedBudget);
  const mgrEntries = Object.entries(managers).sort((a, b) => b[1].style.avgRosterSpend - a[1].style.avgRosterSpend);
  const histSlot$ = recent5.reduce((s, r) => s + r.totalDraft, 0) / recent5.reduce((s, r) => s + POS.reduce((x, p) => x + r[`${p}_n`], 0), 0);

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-6 py-6 space-y-6">
      {/* header */}
      <div className="flex items-center gap-3">
        <div className="p-2 rounded-lg bg-gray-900 text-white"><Target className="w-5 h-5" /></div>
        <div>
          <h1 className="text-xl font-bold text-gray-900">Draft War Room</h1>
          <p className="text-xs text-gray-500 flex items-center gap-1">
            <Lock className="w-3 h-3" /> unlisted page · auctions {seasons[0]}–{lastYear} + 2026 keeper options · built {new Date(data.builtAt).toLocaleDateString()}
          </p>
        </div>
      </div>

      {/* 2026 headline */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Stat icon={DollarSign} label="projected 2026 auction pool" value={fmt$(draft2026.totalBudget)} sub={`of $${league.cap * league.teams} total cap`} />
        <Stat icon={Layers} label="projected open roster slots" value={draft2026.totalSlots} sub={`${league.teams * 17} spots − expected keeps`} />
        <Stat icon={Users} label="expected keepers league-wide" value={Math.round(draft2026.perTeam.reduce((s, t) => s + t.expKeeps, 0))} sub={`last year: ${league.posSpendByYear[league.posSpendByYear.length - 1].keptCount}`} />
        <Stat icon={TrendingUp} label="projected $ per open slot" value={fmt$(draft2026.totalBudget / draft2026.totalSlots)} sub={`5-yr auction avg: ${fmt$(histSlot$)}`} />
      </div>

      {/* positional scarcity board */}
      <Card title="2026 positional scarcity board" subtitle="Expected keepers at each position vs the 5-year norm — more keeps = thinner auction supply. Dollar figures are what those keeps lock away from the pool.">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-3">
          {POS.map((p) => {
            const pool = draft2026.poolByPos[p];
            const delta = pool.expKept - histKept[p].n;
            const badge = scarcityBadge(delta);
            return (
              <div key={p} className="rounded-lg border border-gray-200 p-3">
                <div className="flex items-center justify-between">
                  <span className="text-sm font-bold" style={{ color: POS_COLOR[p] === '#eda100' || POS_COLOR[p] === '#1baf7a' ? INK.primary : POS_COLOR[p] }}>
                    <span className="inline-block w-2.5 h-2.5 rounded-sm mr-1 align-baseline" style={{ background: POS_COLOR[p] }} />{p}
                  </span>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded border ${badge.cls} inline-flex items-center gap-0.5`}>
                    <badge.Icon className="w-3 h-3" />{delta > 0 ? '+' : ''}{delta.toFixed(1)}
                  </span>
                </div>
                <div className="mt-2 space-y-0.5 text-[11px] text-gray-600">
                  <div>exp. keeps <b className="text-gray-900">{pool.expKept}</b> <span className="text-gray-400">(norm {histKept[p].n.toFixed(1)})</span></div>
                  <div>$ locked <b className="text-gray-900">{fmt$(pool.expKept$)}</b> <span className="text-gray-400">(norm {fmt$(histKept[p].$)})</span></div>
                  <div>auction norm <b className="text-gray-900">{pool.histDraftedPerYear}</b> players · {fmt$(pool.histDrafted$PerYear)}</div>
                </div>
                <div className="mt-1 text-[10px] text-gray-500">{badge.txt}</div>
              </div>
            );
          })}
        </div>
      </Card>

      {/* league money flow */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="Where auction dollars go, by year" subtitle="Total $ spent at auction (keepers excluded) — the shrinking bars are the keeper era eating the draft.">
          <PosLegend />
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={spendData} margin={{ top: 4, right: 8, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={INK.grid} />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }} />
              <YAxis tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={fmt$} />
              <Tooltip {...tooltipStyle} formatter={(v, n) => [fmt$(v), n]} />
              {POS.map((p) => (
                <Bar isAnimationActive={false} key={p} dataKey={p} stackId="a" fill={POS_COLOR[p]} stroke="#fff" strokeWidth={1} />
              ))}
            </BarChart>
          </ResponsiveContainer>
        </Card>

        <Card title="Keeper drag on the pool" subtitle="Salary-cap dollars committed to keepers before the auction starts (count labeled on each point).">
          <ResponsiveContainer width="100%" height={260}>
            <LineChart data={keeperDrag} margin={{ top: 16, right: 12, left: -18, bottom: 0 }}>
              <CartesianGrid vertical={false} stroke={INK.grid} />
              <XAxis dataKey="year" tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }} />
              <YAxis tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={fmt$} />
              <Tooltip {...tooltipStyle} formatter={(v, n) => [n === 'kept$' ? fmt$(v) : v, n === 'kept$' ? '$ kept' : 'players kept']} />
              <Line isAnimationActive={false} type="monotone" dataKey="kept$" stroke="#2a78d6" strokeWidth={2} dot={{ r: 3 }} name="kept$"
                label={({ x, y, index }) => {
                  const d = keeperDrag[index];
                  return index % 2 === 0 ? <text x={x} y={y - 8} textAnchor="middle" fontSize={10} fill={INK.secondary}>{d.keptN}</text> : null;
                }} />
            </LineChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* price discovery */}
      <div className="grid lg:grid-cols-2 gap-4">
        <Card title="League price curves (your in-house ADP)" subtitle={`Avg auction cost of the Nth-priciest player at each position, last 5 drafts. K/DEF omitted — they go for $1–2. This is what "the market" pays at each slot.`}>
          <PosLegend />
          <ResponsiveContainer width="100%" height={280}>
            <LineChart data={curveData} margin={{ top: 4, right: 26, left: -18, bottom: 12 }}>
              <CartesianGrid stroke={INK.grid} />
              <XAxis dataKey="rank" tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }}
                label={{ value: 'positional rank (by price)', position: 'insideBottom', offset: -6, fontSize: 11, fill: INK.muted }} />
              <YAxis tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={fmt$} />
              <Tooltip {...tooltipStyle} formatter={(v, n) => [fmt$(v), n]} labelFormatter={(l) => `#${l} at position`} />
              {['QB', 'RB', 'WR', 'TE'].map((p) => (
                <Line isAnimationActive={false} key={p} type="monotone" dataKey={p} stroke={POS_COLOR[p]} strokeWidth={2} dot={false} connectNulls />
              ))}
            </LineChart>
          </ResponsiveContainer>
        </Card>

        <Card title="The $200 shape of the room" subtitle="Avg cost of the Nth-priciest purchase overall (all positions, last 14 drafts). The cliff after the top ~10 is where bargains start.">
          <ResponsiveContainer width="100%" height={280}>
            <AreaChart data={league.slotCurve} margin={{ top: 4, right: 12, left: -18, bottom: 12 }}>
              <CartesianGrid stroke={INK.grid} />
              <XAxis dataKey="overallRank" ticks={[1, 10, 20, 30, 40, 50, 60]} tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }}
                label={{ value: 'overall price rank in the draft', position: 'insideBottom', offset: -6, fontSize: 11, fill: INK.muted }} />
              <YAxis tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={fmt$} />
              <Tooltip {...tooltipStyle} formatter={(v) => [fmt$(v), 'avg cost']} labelFormatter={(l) => `#${l} priciest buy`} />
              <Area isAnimationActive={false} type="monotone" dataKey="avgCost" stroke="#2a78d6" strokeWidth={2} fill="#cde2fb" />
            </AreaChart>
          </ResponsiveContainer>
        </Card>
      </div>

      {/* inflation small multiples */}
      <Card title="Top-of-market inflation by position" subtitle="Avg cost of each year's three priciest auction buys at the position — what elite talent costs, year over year.">
        <div className="grid grid-cols-2 md:grid-cols-3 xl:grid-cols-6 gap-2">
          {POS.map((p) => (
            <div key={p}>
              <div className="text-[11px] font-semibold text-gray-700 mb-0.5 flex items-center gap-1">
                <span className="w-2 h-2 rounded-sm inline-block" style={{ background: POS_COLOR[p] }} />{p}
              </div>
              <ResponsiveContainer width="100%" height={90}>
                <LineChart data={league.inflation} margin={{ top: 4, right: 4, left: 0, bottom: 0 }}>
                  <XAxis dataKey="year" hide />
                  <YAxis hide domain={[0, 'dataMax']} />
                  <Tooltip {...tooltipStyle} formatter={(v) => [fmt$(v), `top-3 avg`]} />
                  <Line isAnimationActive={false} type="monotone" dataKey={p} stroke={POS_COLOR[p]} strokeWidth={2} dot={false} />
                </LineChart>
              </ResponsiveContainer>
              <div className="text-[10px] text-gray-400 flex justify-between"><span>{seasons[0]}</span><span>{lastYear}</span></div>
            </div>
          ))}
        </div>
      </Card>

      {/* manager profiles */}
      <Card title="Manager tendencies" subtitle="Career auction behavior (all rostered $, keepers included, 2012–2025). DNA bars show budget share by position; “recent” = last 3 drafts.">
        <div className="grid md:grid-cols-2 gap-3">
          {mgrEntries.map(([nick, m]) => (
            <div key={nick} className="rounded-lg border border-gray-200 p-3">
              <div className="flex items-baseline justify-between">
                <span className="font-bold text-gray-900">{nick}</span>
                <span className="text-[10px] px-1.5 py-0.5 rounded bg-gray-900 text-white">{m.archetype}</span>
              </div>
              <div className="mt-2 space-y-1">
                <DnaBar mix={m.posPctCareer} label="career" />
                <DnaBar mix={m.posPctRecent} label="recent" />
              </div>
              <div className="mt-2 grid grid-cols-3 gap-x-2 gap-y-1 text-[11px] text-gray-600">
                <div>top-3 share <b className="text-gray-900">{pct(m.style.top3Share)}</b></div>
                <div>$1 flyers/yr <b className="text-gray-900">{m.style.dollarPlayersPerYear}</b></div>
                <div>overpay idx <b className={m.style.overpayIndex > 0 ? 'text-rose-700' : 'text-emerald-700'}>{m.style.overpayIndex > 0 ? '+' : ''}{m.style.overpayIndex}</b></div>
                <div>keeps/yr <b className="text-gray-900">{m.keepers.perYear}</b></div>
                <div>avg keep $ <b className="text-gray-900">{fmt$(m.keepers.avgCost)}</b></div>
                <div>FAAB avg bid <b className="text-gray-900">{fmt$(m.faab.avgBid)}</b></div>
              </div>
              <div className="mt-1.5 text-[11px] text-gray-500">
                biggest swing: <b className="text-gray-700">{m.style.biggestBuy.player}</b> ({fmt$(m.style.biggestBuy.cost)}, {m.style.biggestBuy.year})
              </div>
            </div>
          ))}
        </div>
        <p className="mt-3 text-[11px] text-gray-400">
          Overpay index = avg $ paid above the league's price curve for the same positional rank ($5+ buys only). Positive = pays the premium; negative = waits out the room.
        </p>
      </Card>

      {/* 2026 team-by-team */}
      <Card title="2026 budgets, team by team" subtitle="Keep probabilities blend last offseason's behavior at each price point with the player's market-value surplus (national ADP → historical league price vs his keep cost); mid-contract players ≈ 95%. Expand a team for the option-by-option view.">
        <div className="overflow-x-auto">
          <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
            <thead>
              <tr className="text-[11px] text-gray-500 border-b border-gray-200">
                <th className="text-left py-1.5 font-medium">team</th>
                <th className="text-right px-2 font-medium">options</th>
                <th className="text-right px-2 font-medium">exp. keeps</th>
                <th className="text-right px-2 font-medium">exp. keep $</th>
                <th className="text-right px-2 font-medium">pen/adj</th>
                <th className="text-right px-2 font-medium">proj. budget</th>
                <th className="w-6" />
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <React.Fragment key={t.nick}>
                  <tr className="border-b border-gray-100 hover:bg-gray-50 cursor-pointer" onClick={() => setOpenTeam(openTeam === t.nick ? null : t.nick)}>
                    <td className="py-2 font-semibold text-gray-900">{t.nick} <span className="font-normal text-gray-400 text-xs hidden sm:inline">· {t.teamName}</span></td>
                    <td className="text-right px-2 text-gray-600">{t.optionCount}</td>
                    <td className="text-right px-2 text-gray-600">{t.expKeeps}</td>
                    <td className="text-right px-2 text-gray-600">{fmt$(t.expCommitted$)}</td>
                    <td className="text-right px-2 text-gray-600">{t.adjustments$ - t.penalties$ >= 0 ? '+' : ''}{t.adjustments$ - t.penalties$}</td>
                    <td className="text-right px-2 font-bold text-gray-900">{fmt$(t.projectedBudget)}</td>
                    <td className="text-right text-gray-400">{openTeam === t.nick ? <ChevronDown className="w-4 h-4 inline" /> : <ChevronRight className="w-4 h-4 inline" />}</td>
                  </tr>
                  {openTeam === t.nick && (
                    <tr>
                      <td colSpan={7} className="bg-gray-50 px-3 py-2">
                        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-x-4 gap-y-0.5">
                          {t.options.map((o) => (
                            <div key={o.player} className="flex items-center gap-1.5 text-[12px] py-0.5">
                              <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: POS_COLOR[o.pos] || '#c3c2b7' }} />
                              <span className="text-gray-800 truncate">{o.player}</span>
                              <span className="text-gray-400">{o.pos}</span>
                              <span className="ml-auto text-gray-600">{fmt$(o.keepCost || 0)}</span>
                              <ProbStrip d={o.probDist} w={44} />
                              <span className={`w-10 text-right font-semibold ${o.keepProb >= 0.6 ? 'text-emerald-700' : o.keepProb >= 0.3 ? 'text-gray-600' : 'text-gray-400'}`}>{pct(o.keepProb)}</span>
                              {o.contract && <Lock className="w-3 h-3 text-amber-500" title="on contract" />}
                            </div>
                          ))}
                        </div>
                      </td>
                    </tr>
                  )}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      {/* keep probability model */}
      <Card title="What gets kept, by price" subtitle="Share of last offseason's keeper options at each keep-cost that were actually kept (n on each bar). The $6–10 trough is the +$5'd waiver-wire mass — mostly let go.">
        <ResponsiveContainer width="100%" height={200}>
          <BarChart data={probData} margin={{ top: 18, right: 8, left: -24, bottom: 0 }}>
            <CartesianGrid vertical={false} stroke={INK.grid} />
            <XAxis dataKey="bucket" tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }} />
            <YAxis tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} domain={[0, 100]} />
            <Tooltip {...tooltipStyle} formatter={(v, n, e) => [`${v}% kept (n=${e.payload.n})`, 'keep rate']} />
            <Bar isAnimationActive={false} dataKey="p" fill="#2a78d6" radius={[4, 4, 0, 0]}
              label={({ x, y, width, index }) => (
                <text x={x + width / 2} y={y - 6} textAnchor="middle" fontSize={10} fill={INK.secondary}>n={probData[index].n}</text>
              )} />
          </BarChart>
        </ResponsiveContainer>
      </Card>

      {/* ---- market ADP comparison ---- */}
      {market && (
        <>
          <div className="flex items-center gap-2 pt-2">
            <div className="h-px flex-1 bg-gray-200" />
            <span className="text-xs font-bold text-gray-500 uppercase tracking-wide">vs the national market</span>
            <div className="h-px flex-1 bg-gray-200" />
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="Where this league defies the market" subtitle={`Avg $ paid above (red) or below (blue) the market-implied price per auction buy, ${market.yearsCovered[0]}–${market.yearsCovered[market.yearsCovered.length - 1]}. Implied price = what the room paid at that player's national-ADP rank.`}>
              <ResponsiveContainer width="100%" height={200}>
                <BarChart layout="vertical" data={POS.map((p) => ({ pos: p, avg: market.posBias[p].avg, recent: market.posBias[p].recent }))} margin={{ top: 4, right: 72, left: 56, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke={INK.grid} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v > 0 ? '+' : ''}$${v}`} />
                  <YAxis type="category" dataKey="pos" interval={0} tick={{ fontSize: 12, fill: INK.secondary }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }} width={44} />
                  <Tooltip {...tooltipStyle} formatter={(v, n) => [`${v > 0 ? '+' : ''}$${v} per buy`, n === 'avg' ? 'all-time' : 'last 5 drafts']} />
                  <Bar isAnimationActive={false} dataKey="avg" radius={[0, 4, 4, 0]} label={({ x, y, width, height, index }) => {
                    const d = POS.map((p) => market.posBias[p])[index];
                    const right = Math.max(x, x + width);
                    return (
                      <text x={right + 4} y={y + height / 2 + 3} fontSize={10}
                        textAnchor="start" fill={INK.secondary}>
                        {d.recent > 0 ? '+' : ''}{d.recent} recent
                      </text>
                    );
                  }}>
                    {POS.map((p) => (
                      <Cell key={p} fill={market.posBias[p].avg >= 0 ? '#e34948' : '#2a78d6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
              <p className="text-[11px] text-gray-400">Red = the room pays a premium at that position; blue = the room lets it come at a discount.</p>
            </Card>

            <Card title="Who buys ahead of the market" subtitle="Avg $ vs market-implied price per meaningful buy ($5+), career. Reach rate = share of $10+ buys paid $5+ over implied.">
              <ResponsiveContainer width="100%" height={280}>
                <BarChart layout="vertical" data={Object.entries(market.mgrDelta).map(([o, x]) => ({ owner: o, ...x })).sort((a, b) => b.avg - a.avg)} margin={{ top: 4, right: 56, left: 36, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke={INK.grid} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: INK.muted }} tickLine={false} axisLine={false} tickFormatter={(v) => `${v > 0 ? '+' : ''}$${v}`} />
                  <YAxis type="category" dataKey="owner" interval={0} tick={{ fontSize: 12, fill: INK.secondary }} tickLine={false} axisLine={{ stroke: '#c3c2b7' }} width={58} />
                  <Tooltip {...tooltipStyle} formatter={(v, n, e) => [`${v > 0 ? '+' : ''}$${v}/buy · reaches on ${Math.round(e.payload.reachRate * 100)}% of $10+ buys`, 'vs market']} />
                  <Bar isAnimationActive={false} dataKey="avg" radius={[0, 4, 4, 0]} label={({ x, y, width, height, index }) => {
                    const rows = Object.entries(market.mgrDelta).map(([o, x2]) => x2).sort((a, b) => b.avg - a.avg);
                    const right = Math.max(x, x + width);
                    return (
                      <text x={right + 4} y={y + height / 2 + 3} fontSize={10}
                        textAnchor="start" fill={INK.muted}>
                        {Math.round(rows[index].reachRate * 100)}%
                      </text>
                    );
                  }}>
                    {Object.entries(market.mgrDelta).sort((a, b) => b[1].avg - a[1].avg).map(([o, x]) => (
                      <Cell key={o} fill={x.avg >= 0 ? '#e34948' : '#2a78d6'} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </Card>
          </div>

          <div className="grid lg:grid-cols-2 gap-4">
            <Card title="Hall of reaches" subtitle="Biggest premiums ever paid over the market-implied price.">
              <div className="space-y-1">
                {market.reaches.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px] py-0.5 border-b border-gray-50">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: POS_COLOR[r.pos] || '#c3c2b7' }} />
                    <span className="text-gray-800 font-medium">{r.player}</span>
                    <span className="text-gray-400">{r.year} · {r.owner}</span>
                    <span className="ml-auto text-gray-500">paid ${r.paid} vs ${r.implied}</span>
                    <span className="w-12 text-right font-bold text-rose-700">+${r.delta}</span>
                  </div>
                ))}
              </div>
            </Card>
            <Card title="Hall of steals" subtitle="Biggest discounts ever captured under the market-implied price.">
              <div className="space-y-1">
                {market.steals.map((r, i) => (
                  <div key={i} className="flex items-center gap-2 text-[12px] py-0.5 border-b border-gray-50">
                    <span className="w-2 h-2 rounded-sm flex-shrink-0" style={{ background: POS_COLOR[r.pos] || '#c3c2b7' }} />
                    <span className="text-gray-800 font-medium">{r.player}</span>
                    <span className="text-gray-400">{r.year} · {r.owner}</span>
                    <span className="ml-auto text-gray-500">paid ${r.paid} vs ${r.implied}</span>
                    <span className="w-12 text-right font-bold text-blue-700">−${-r.delta}</span>
                  </div>
                ))}
              </div>
            </Card>
          </div>

          {market.pool2026 && (
            <Card title="2026 market board vs your keeper pool" subtitle="This month's national mock-draft board crossed with who holds each player. Expected $ = what this league pays at that ADP, scaled by this year's positional supply/demand factor; P(kept) weighs price behavior and value surplus together.">
              {market.posFactors && (
                <div className="mb-3 flex flex-wrap gap-2">
                  {POS.map((p) => {
                    const f = market.posFactors[p];
                    if (!f) return null;
                    const hot = f.factor >= 1.05;
                    const cold = f.factor <= 0.95;
                    return (
                      <span key={p} title={`projected $${f.demand$} chasing ~${f.projN} players (hist $${f.hist$} over ${f.histN})`}
                        className={`text-[11px] px-2 py-1 rounded-md border inline-flex items-center gap-1.5 ${hot ? 'bg-rose-50 border-rose-200 text-rose-800' : cold ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-gray-50 border-gray-200 text-gray-600'}`}>
                        <span className="w-2 h-2 rounded-sm" style={{ background: POS_COLOR[p] }} />
                        <b>{p}</b> ×{f.factor.toFixed(2)}
                        <span className="opacity-70">{hot ? 'inflated' : cold ? 'deflated' : 'normal'}</span>
                      </span>
                    );
                  })}
                </div>
              )}
              <div className="overflow-x-auto">
                <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                  <thead>
                    <tr className="text-[11px] text-gray-500 border-b border-gray-200">
                      <th className="text-left py-1.5 font-medium">ADP</th>
                      <th className="text-left px-2 font-medium">player</th>
                      <th className="text-left px-2 font-medium">held by</th>
                      <th className="text-right px-2 font-medium">keep $</th>
                      <th className="text-right px-2 font-medium">P(kept)</th>
                      <th className="text-right px-2 font-medium">base $</th>
                      <th className="text-right px-2 font-medium">expected $</th>
                    </tr>
                  </thead>
                  <tbody>
                    {market.pool2026.slice(0, 25).map((p, i) => (
                      <tr key={i} className={`border-b border-gray-50 ${p.keepProb < 0.5 ? '' : 'opacity-50'}`}>
                        <td className="py-1 text-gray-500">{p.adp}</td>
                        <td className="px-2 font-medium text-gray-900">
                          <span className="w-2 h-2 rounded-sm inline-block mr-1.5" style={{ background: POS_COLOR[p.pos] || '#c3c2b7' }} />
                          {p.player} <span className="text-gray-400 font-normal">{p.pos}</span>
                        </td>
                        <td className="px-2 text-gray-600">{p.ownedBy}</td>
                        <td className="px-2 text-right text-gray-600">${p.keepCost}</td>
                        <td className="px-2 text-right text-gray-600">{pct(p.keepProb)}</td>
                        <td className="px-2 text-right text-gray-400">{p.basePrice != null ? fmt$(p.basePrice) : '—'}</td>
                        <td className="px-2 text-right font-semibold text-gray-900">{p.expPrice != null ? fmt$(p.expPrice) : <span className="text-gray-400 font-normal">likely kept</span>}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <p className="mt-2 text-[11px] text-gray-400">
                Base $ = what this league has historically paid a player at that ADP when he reaches the auction (piecewise fit over ~1,300 matched buys); the factor
                folds in this year's dollars chasing the position (each team's positional DNA × projected budget, net of keeps) and the keep surplus/deficit vs the
                5-yr norm. P(kept) blends last offseason's keep rate at that price with the value surplus (expected $ − keep $), iterated so prices and keep decisions
                agree; the league-wide total stays anchored to ~77 keeps.
              </p>
            </Card>
          )}

          {/* keep-likelihood decomposition, position by position */}
          {market.posFactors && (() => {
            const allOpts = draft2026.perTeam.flatMap((t) => t.options.filter((o) => o.adp !== undefined));
            // avail rank is always within the player's position over the FULL
            // board, so a name search can't distort it
            const availCounters = {};
            const decoratedAll = allOpts.slice().sort((a, b) => a.adp - b.adp).map((o) => {
              const available = o.keepProb < 0.5;
              if (available) availCounters[o.pos] = (availCounters[o.pos] || 0) + 1;
              return { ...o, availRank: available ? availCounters[o.pos] : null };
            });
            const q = playerQuery.trim().toLowerCase();
            const searching = q.length > 0;
            const decorated = searching
              ? decoratedAll.filter((o) => o.player.toLowerCase().includes(q) || (o.ownedBy || '').toLowerCase().includes(q))
              : decoratedAll.filter((o) => o.pos === posTab);
            const topAvail = decorated.find((o) => o.pos === posTab && o.availRank === 1);
            const f = market.posFactors[posTab];
            const norm = histKept[posTab] || { n: 0 };
            const pool = draft2026.poolByPos[posTab];
            return (
              <Card title="How keep likelihood is built, position by position" subtitle="Every market-board player at the position: the empirical price prior, the value surplus that moves it, and how the position's scarcity factor turns his ADP-implied base price into an expected cost.">
                <div className="flex flex-wrap items-center gap-1.5 mb-3">
                  {POS.map((p) => (
                    <button key={p} onClick={() => setPosTab(p)} disabled={searching}
                      className={`px-2.5 py-1 rounded-md text-xs font-semibold border transition-colors ${searching ? 'opacity-40 cursor-not-allowed bg-white text-gray-400 border-gray-200' : posTab === p ? 'bg-gray-900 text-white border-gray-900' : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'}`}>
                      <span className="w-2 h-2 rounded-sm inline-block mr-1" style={{ background: POS_COLOR[p] }} />{p}
                    </button>
                  ))}
                  <div className="relative ml-auto">
                    <input
                      type="text"
                      value={playerQuery}
                      onChange={(e) => setPlayerQuery(e.target.value)}
                      placeholder="search player or manager…"
                      className="text-xs border border-gray-200 rounded-md pl-2.5 pr-7 py-1.5 w-52 focus:outline-none focus:border-gray-400 placeholder-gray-400"
                    />
                    {searching && (
                      <button onClick={() => setPlayerQuery('')} aria-label="clear search"
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-700 text-sm leading-none">
                        ×
                      </button>
                    )}
                  </div>
                </div>
                {searching && (
                  <p className="mb-2 text-[11px] text-gray-500">
                    Searching all positions — {decorated.length} match{decorated.length === 1 ? '' : 'es'}.
                  </p>
                )}
                {!searching && f && (
                  <div className="mb-3 text-[12px] text-gray-600 bg-gray-50 border border-gray-200 rounded-lg px-3 py-2">
                    <b className="text-gray-900">{posTab} scarcity math:</b> expected keeps <b className="text-gray-900">{pool.expKept}</b> vs {norm.n.toFixed(1)} norm ·
                    projected <b className="text-gray-900">${f.demand$}</b> chasing ~{f.projN} auctioned players (history: ${f.hist$} over {f.histN}) →
                    prices scale <b className={f.factor >= 1.05 ? 'text-rose-700' : f.factor <= 0.95 ? 'text-blue-700' : 'text-gray-900'}>×{f.factor.toFixed(2)}</b>
                    {topAvail && <> · top projected available: <b className="text-gray-900">{topAvail.player}</b> ({posTab}{topAvail.posRank}, exp. {fmt$(topAvail.expPrice)})</>}
                  </div>
                )}
                {!searching && market.rankCurves && market.rankCurves[posTab] && (
                  <div className="mb-3 flex flex-wrap gap-1.5 items-center">
                    <span className="text-[11px] text-gray-500 py-0.5">what the room pays the market-{posTab}<i>n</i> (auctions + pricey keeps, 2013+):</span>
                    {market.rankCurves[posTab].map((b) => (
                      <span key={b.lo} className="text-[11px] px-1.5 py-0.5 rounded border border-gray-200 bg-white text-gray-700"
                        title={`${b.n - (b.nKeep || 0)} auction buys + ${b.nKeep || 0} keeps at $20+`}>
                        #{b.lo}–{b.hi} <b>${Math.round(b.v)}</b><span className="text-gray-400 ml-0.5">n{b.n}</span>
                      </span>
                    ))}
                  </div>
                )}
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" style={{ fontVariantNumeric: 'tabular-nums' }}>
                    <thead>
                      <tr className="text-[11px] text-gray-500 border-b border-gray-200">
                        <th className="text-left py-1.5 font-medium">ADP</th>
                        <th className="text-left px-2 font-medium">rank</th>
                        <th className="text-left px-2 font-medium">player</th>
                        <th className="text-left px-2 font-medium">held by</th>
                        <th className="text-right px-2 font-medium">rank value $</th>
                        <th className="text-right px-2 font-medium">scarcity</th>
                        <th className="text-right px-2 font-medium">expected $</th>
                        <th className="text-right px-2 font-medium">keep $</th>
                        <th className="text-right px-2 font-medium">surplus</th>
                        <th className="text-right px-2 font-medium">price prior</th>
                        <th className="text-right px-2 font-medium">P(kept)</th>
                        <th className="text-right px-2 font-medium">avail #</th>
                      </tr>
                    </thead>
                    <tbody>
                      {decorated.map((o, i) => {
                        const surplus = (o.expPrice || 0) - (o.keepCost || 0);
                        const isTop = o.availRank === 1;
                        return (
                          <tr key={i} className={`border-b border-gray-50 ${isTop ? 'bg-amber-50' : ''}`}>
                            <td className="py-1 text-gray-500">{o.adp}</td>
                            <td className="px-2 text-gray-700 font-semibold">{o.pos}{o.posRank}</td>
                            <td className="px-2 font-medium text-gray-900">
                              {o.player}
                              {o.lock && <Lock className="w-3 h-3 text-amber-500 inline ml-1" title="mid-contract" />}
                              {isTop && <span className="ml-1.5 text-[10px] px-1.5 py-0.5 rounded bg-amber-200 text-amber-900 font-semibold">top available</span>}
                            </td>
                            <td className="px-2 text-gray-600">{o.ownedBy}</td>
                            <td className="px-2 text-right text-gray-500">{fmt$(o.basePrice)}</td>
                            <td className="px-2 text-right text-gray-500">×{(market.posFactors[o.pos] || { factor: 1 }).factor.toFixed(2)}</td>
                            <td className="px-2 text-right font-semibold text-gray-900">{fmt$(o.expPrice)}</td>
                            <td className="px-2 text-right text-gray-600">${o.keepCost}</td>
                            <td className={`px-2 text-right font-medium ${surplus > 3 ? 'text-emerald-700' : surplus < -3 ? 'text-rose-700' : 'text-gray-500'}`}>{surplus > 0 ? '+' : ''}{surplus}</td>
                            <td className="px-2 text-right text-gray-500">{pct(o.bucketProb)}</td>
                            <td className="px-2 text-right">
                              <div className="flex items-center justify-end gap-1.5">
                                <ProbStrip d={o.probDist} />
                                <span className={`font-bold w-9 ${o.keepProb >= 0.6 ? 'text-emerald-700' : o.keepProb < 0.35 ? 'text-gray-400' : 'text-gray-800'}`}>{pct(o.keepProb)}</span>
                              </div>
                            </td>
                            <td className="px-2 text-right text-gray-500">{o.availRank ? `#${o.availRank}` : '—'}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
                <p className="mt-2 text-[11px] text-gray-400">
                  Reading a row, left to right: the player's market positional rank (national ADP) → what this league pays that rank (auction prices plus keeps at
                  $20+, which are revealed valuations; 2012's pre-keeper prices excluded) → this year's {posTab} scarcity multiplier → his expected auction price.
                  Compare that to his keep cost: the surplus shifts the empirical price prior into the final P(kept). “Avail #” orders the players the model expects
                  to actually reach the auction (P&lt;50%). The strip beside each probability is the model's uncertainty — 400 Monte-Carlo draws resampling the prior
                  (Beta over its sample size) and the rank value (the spread of historical prices at that rank): light band 5–95%, dark band 25–75%, tick = median.
                  A wide strip means the call genuinely hinges on how you read the evidence; a narrow one is settled.
                </p>
              </Card>
            );
          })()}
        </>
      )}

      <p className="text-[11px] text-gray-400 pb-6">
        Sources: BoBData Keeper Data (auctions + keepers 2012–2025), keepers2026.json (2026 options, contracts, penalties, adjustments),
        BoB-Keepers-2025 workbook (empirical keep rates), Yahoo transactions (FAAB)
        {market && <>, {market.source} ({market.yearsCovered.filter((y) => y < 2026).length} seasons joined at a {pct(market.matchRate)} match rate; 2025 archive unavailable; 12-team standard mocks — same format as this league)</>}.
        {' '}Rebuild with <code>node scripts/build/fetchAdp.js</code> + <code>node scripts/build/buildDraftIntel.js</code>.
      </p>
    </div>
  );
}
